import { lazy, Suspense } from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useStartupData } from "@/modules/startup/stores";
import { useUserDataFlattened } from "@/modules/user/stores";
import { useCurrencySymbol } from "@/hooks/useUtils";
import { useNavigation, ROUTES } from "@/utils/navigation";
import utils from "@/utils/utils";

const RegisterTurntableDialog = lazy(() =>
  import("../components/RegisterTurntable/RegisterTurntableDialog").then(m => ({
    default: m.RegisterTurntableDialog,
  }))
);

let hasShownRegisterTurntable = utils.isJustLoggedOut();

export const useRegisterTurntablePopup = () => {
  const { data: startupData, loading } = useStartupData();
  const { isAuthenticated } = useUserDataFlattened();
  const currencySymbol = useCurrencySymbol();
  const { navigate } = useNavigation();

  const isStartupReady = loading === "success";
  const enableRegisterTurntable = startupData?.enableRegisterTurntable;
  const registerTurntableList = startupData?.registerTurntableList;
  const turntableIconFileId = startupData?.turntableIconFileId;

  const handleGoRegister = (onComplete: () => void) => {
    hasShownRegisterTurntable = true;
    onComplete();
    navigate(ROUTES.REGISTER);
  };

  useQueuedPopup({
    popupId: POPUP_CONFIGS.REGISTER_TURNTABLE.id,
    priority: POPUP_CONFIGS.REGISTER_TURNTABLE.priority,
    shouldShow: () => {
      if (hasShownRegisterTurntable) return false;
      if (!isStartupReady) return false;
      if (isAuthenticated) return false;
      if (!enableRegisterTurntable) return false;
      return true;
    },
    renderContent: onComplete => (
      <Suspense fallback={null}>
        <RegisterTurntableDialog
          turntableList={registerTurntableList}
          turntableIconFileId={turntableIconFileId}
          currencySymbol={currencySymbol}
          onComplete={() => {
            hasShownRegisterTurntable = true;
            onComplete();
          }}
          onGoRegister={() => handleGoRegister(onComplete)}
        />
      </Suspense>
    ),
    onComplete: () => {
      hasShownRegisterTurntable = true;
    },
    showOnce: false,
    autoTrigger: true,
    dependencies: [isStartupReady, enableRegisterTurntable, isAuthenticated],
  });
};
